import { Box, Button, Flex, Grid, GridItem, Heading, HStack, Icon, Image, Text } from "@chakra-ui/react";
import { motion, useMotionTemplate, useMotionValue, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useRef, type MouseEvent, type ReactNode } from "react";
import { FiArrowDown, FiArrowUpRight, FiMapPin } from "react-icons/fi";
import { portfolio } from "../data/portfolio";
import { PhoneShowcase } from "./PhoneShowcase";
import { Accent, EASE_OUT } from "./SectionHeading";

const MotionBox = motion(Box);
const MotionSpan = motion.span;

const headlineLines: ReactNode[] = [
  "Um código.",
  <Accent key="accent">Mais de 60 apps</Accent>,
  "em produção.",
];

function fadeUp(delay: number) {
  return {
    initial: { opacity: 0, y: 22 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6, delay, ease: EASE_OUT },
  };
}

export function HeroSection() {
  const { person } = portfolio;
  const prefersReducedMotion = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);

  const mouseX = useMotionValue(70);
  const mouseY = useMotionValue(30);
  const cursorGlow = useMotionTemplate`radial-gradient(620px circle at ${mouseX}% ${mouseY}%, rgba(232,164,74,0.10), transparent 65%)`;

  // The hero recedes as the next chapter scrolls over it.
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const contentOpacity = useTransform(scrollYProgress, [0.6, 1], [1, 0.1]);

  function handleMouseMove(event: MouseEvent<HTMLDivElement>) {
    if (prefersReducedMotion) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    mouseX.set(((event.clientX - bounds.left) / bounds.width) * 100);
    mouseY.set(((event.clientY - bounds.top) / bounds.height) * 100);
  }

  return (
    <Box
      ref={ref}
      id="inicio"
      as="section"
      position="relative"
      overflow="hidden"
      minH="100vh"
      display="flex"
      alignItems="center"
      onMouseMove={handleMouseMove}
    >
      <Box
        position="absolute"
        inset={0}
        opacity={0.5}
        bgImage="radial-gradient(rgba(255,255,255,0.09) 1px, transparent 1px)"
        bgSize="28px 28px"
        sx={{ maskImage: "radial-gradient(ellipse 70% 60% at 65% 40%, black, transparent)" }}
        aria-hidden
      />
      {!prefersReducedMotion ? (
        <MotionBox position="absolute" inset={0} pointerEvents="none" style={{ background: cursorGlow }} aria-hidden />
      ) : null}

      <MotionBox
        position="relative"
        maxW="container.xl"
        mx="auto"
        w="full"
        px={{ base: 5, md: 8 }}
        pt={{ base: "120px", lg: "128px" }}
        pb={{ base: 16, lg: 20 }}
        style={prefersReducedMotion ? undefined : { y: contentY, opacity: contentOpacity }}
      >
        <Grid templateColumns={{ base: "1fr", lg: "minmax(0, 7fr) minmax(0, 5fr)" }} gap={{ base: 16, lg: 10 }} alignItems="center">
          <GridItem>
            <MotionBox {...fadeUp(0.05)}>
              <HStack spacing={3} mb={{ base: 7, md: 9 }}>
                <Image
                  src={person.profilePhoto}
                  alt={`Retrato de ${person.fullName}`}
                  boxSize="46px"
                  borderRadius="full"
                  objectFit="cover"
                  borderWidth="1px"
                  borderColor="whiteAlpha.400"
                />
                <Box>
                  <Text fontWeight="600" color="white" lineHeight="short">
                    {person.fullName}
                  </Text>
                  <Text fontFamily="mono" fontSize="xs" letterSpacing="0.06em" color="graphite.400">
                    {person.headline}
                  </Text>
                </Box>
              </HStack>
            </MotionBox>

            <Heading
              as="h1"
              fontSize={{ base: "clamp(2.25rem, 12.2vw, 2.9rem)", sm: "3.75rem", md: "4.75rem", xl: "5.5rem" }}
              lineHeight="1.02"
              letterSpacing="-0.04em"
              color="white"
            >
              {headlineLines.map((line, i) => (
                <Box as="span" key={i} display="block" overflow="hidden" pb="0.12em" mb="-0.12em">
                  <MotionSpan
                    style={{ display: "block" }}
                    initial={{ y: "112%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.9, delay: 0.15 + i * 0.11, ease: EASE_OUT }}
                  >
                    {line}
                  </MotionSpan>
                </Box>
              ))}
            </Heading>

            <MotionBox {...fadeUp(0.6)}>
              <Text mt={{ base: 6, md: 8 }} fontSize={{ base: "md", md: "xl" }} lineHeight="tall" color="graphite.300" maxW="xl">
                {person.tagline}
              </Text>
            </MotionBox>

            <MotionBox {...fadeUp(0.7)}>
              <Flex gap={3} mt={8} flexWrap="wrap">
                <Button
                  as="a"
                  href="#contato"
                  colorScheme="accent"
                  size="lg"
                  borderRadius="full"
                  rightIcon={<FiArrowUpRight />}
                  boxShadow="0 12px 34px rgba(224, 134, 44, 0.3)"
                  transition="transform 0.25s ease, box-shadow 0.25s ease"
                  _hover={{ transform: "translateY(-2px)", boxShadow: "0 18px 40px rgba(224, 134, 44, 0.42)" }}
                >
                  Entrar em contato
                </Button>
                <Button
                  as="a"
                  href="#experiencia"
                  variant="outline"
                  size="lg"
                  borderRadius="full"
                  borderColor="whiteAlpha.400"
                  color="graphite.100"
                  _hover={{ bg: "whiteAlpha.100", borderColor: "whiteAlpha.600" }}
                >
                  Ver trajetória
                </Button>
              </Flex>
            </MotionBox>

            <MotionBox {...fadeUp(0.8)}>
              <Flex columnGap={5} rowGap={2} mt={9} flexWrap="wrap" fontSize="sm" color="graphite.400">
                <HStack spacing={2}>
                  <Icon as={FiMapPin} />
                  <Text as="span">{person.location}</Text>
                </HStack>
                <HStack spacing={2} align="flex-start">
                  <Box position="relative" w="8px" h="8px" mt="6px" flexShrink={0}>
                    <MotionBox
                      position="absolute"
                      inset={0}
                      borderRadius="full"
                      bg="green.300"
                      animate={{ scale: [1, 2.6], opacity: [0.7, 0] }}
                      transition={{ duration: 1.8, repeat: Infinity, ease: "easeOut" }}
                    />
                    <Box position="absolute" inset={0} borderRadius="full" bg="green.300" />
                  </Box>
                  <Text as="span" color="graphite.200">
                    {person.availability}
                  </Text>
                </HStack>
              </Flex>
            </MotionBox>
          </GridItem>

          <GridItem>
            <PhoneShowcase />
          </GridItem>
        </Grid>

        <MotionBox {...fadeUp(1.2)} display={{ base: "none", lg: "block" }} mt={14}>
          <HStack as="a" href="#skills" spacing={3} w="fit-content" fontFamily="mono" fontSize="xs" letterSpacing="0.14em" textTransform="uppercase" color="graphite.400" _hover={{ color: "accent.300" }}>
            <MotionBox animate={{ y: [0, 5, 0] }} transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}>
              <Icon as={FiArrowDown} display="block" />
            </MotionBox>
            <Text as="span">Como isso chega às lojas</Text>
          </HStack>
        </MotionBox>
      </MotionBox>
    </Box>
  );
}
